import React, { useState } from 'react';
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from "../ToolRenderers";
import { ToolRendererProps } from "../ToolRenderers";
import { DataTable } from "../../ui/datatable/datatable";
import { Badge } from "../../ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { ChevronDown, ChevronRight } from 'lucide-react';
import PrettyYamlViewer from "../../K8sInfo/YamlViewer";

interface K8sResourceSummary {
  environment: string;
  kind: string;
  resourceYaml: string;
  name?: string;
  namespace?: string;
  yamlSearchText?: string; // For the DataTable's text search
}

interface ServiceYamlResponse {
  k8sResourceSummary: K8sResourceSummary[];
}

// Function to extract name and namespace from YAML
const extractMetadata = (yamlString: string): { name: string; namespace: string } => {
  const metadata = {
    name: 'Unknown',
    namespace: 'Unknown'
  };

  try {
    const nameMatch = yamlString.match(/name:\s+"?([^"\s]+)"?/);
    if (nameMatch && nameMatch[1]) {
      metadata.name = nameMatch[1];
    }

    const namespaceMatch = yamlString.match(/namespace:\s+"?([^"\s]+)"?/);
    if (namespaceMatch && namespaceMatch[1]) {
      metadata.namespace = namespaceMatch[1];
    }
  } catch (error) {
    console.error('Error extracting metadata from YAML:', error);
  }

  return metadata;
};

export function ServiceYamlToolRenderer({ message, isToolCall }: ToolRendererProps) {
  // We only want to render the response, not the call
  if (!message.tool_call_response_content) {
    // This is a tool call, do the default rendering
    return <DefaultToolCallRenderer message={message} />;
  }

  let response: ServiceYamlResponse | null = null;
  try {
    response = JSON.parse(message.tool_call_response_content);
  } catch (error) {
    console.error('Error parsing service YAML response data:', error);
    return null;
  }

  // Validate response
  if (!response || !response.k8sResourceSummary || !Array.isArray(response.k8sResourceSummary) || response.k8sResourceSummary.length === 0) {
    return null;
  }

  // Enrich resources with extracted name and namespace
  const enrichedResources = response.k8sResourceSummary.map(resource => {
    const metadata = extractMetadata(resource.resourceYaml);
    return {
      ...resource,
      name: metadata.name,
      namespace: metadata.namespace,
      yamlSearchText: resource.resourceYaml // Include YAML content for text search
    };
  });

  // Define columns for the DataTable
  const columns: ColumnDef<K8sResourceSummary>[] = [
    {
      id: "kind",
      accessorKey: "kind",
      header: "Resource Type",
      size: 150,
      cell: ({ row }) => (
        <Badge className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/20">
          {row.original.kind}
        </Badge>
      ),
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 200,
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    {
      id: "namespace",
      accessorKey: "namespace",
      header: "Namespace",
      size: 150,
      cell: ({ row }) => <div>{row.original.namespace}</div>,
    },
    {
      id: "environment",
      accessorKey: "environment",
      header: "Environment",
      size: 150,
      cell: ({ row }) => <div>{row.original.environment}</div>,
    },
    {
      id: "yamlSearchText",
      accessorKey: "yamlSearchText",
      header: "",
      size: 0,
      cell: () => null, // Hidden column just for search
    },
    {
      id: "yaml",
      header: "YAML",
      size: 150,
      cell: ({ row }) => {
        const [isOpen, setIsOpen] = useState(false);
        
        return (
          <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-textmedium hover:text-textlight transition-colors">
              {isOpen ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
              <span>View YAML</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 rounded p-0 text-sm font-mono text-textmedium overflow-x-auto max-h-96">
                <PrettyYamlViewer yamlString={row.original.resourceYaml} borderless={true} />
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      }
    }
  ];

  return (
    <div className="w-full border rounded">
      <DataTable
        columns={columns}
        data={enrichedResources}
        tableTitle="Kubernetes Resources"
        filters={[]}
        showPagination={true}
        removeBorder={true}
        textFilteringColumnName="yamlSearchText"
        textFilteringPlaceholder="Search YAML content..."
        columnConfigs={[
          { id: "kind", isFilterable: true },
          { id: "namespace", isFilterable: true },
          { id: "environment", isFilterable: true },
          { id: "yamlSearchText", hiddenByDefault: true, isFilterable: true }
        ]}
      />
    </div>
  );
} 